import { useState } from 'react';
import type { OnboardingTier } from '@/pages/OnboardingHub';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  FileText,
  FileSpreadsheet,
  ClipboardList,
  Upload,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  tier: OnboardingTier;
  onComplete: () => void;
}

interface UploadZone {
  id: string;
  title: string;
  description: string;
  icon: typeof FileText;
  accepts: string;
  hint: string;
  required: boolean;
  uploaded: number;
  tier: OnboardingTier[];
}

export function DataIngestionPortal({ tier, onComplete }: Props) {
  const [zones, setZones] = useState<UploadZone[]>([
    {
      id: 'op_notes',
      title: 'Historical Operative Notes',
      description: 'Upload past op notes so LINK can learn dictation style, CPT patterns, and documentation preferences.',
      icon: FileText,
      accepts: '.pdf,.txt,.doc,.docx',
      hint: 'PDF or Text — recommended: last 50–100 cases',
      required: true,
      uploaded: 0,
      tier: ['single_surgeon', 'small_practice', 'enterprise'],
    },
    {
      id: '837_files',
      title: 'Past Billing 837 Files',
      description: 'Import claim history to identify CPT/ICD coding patterns and payer-specific approval rates.',
      icon: FileSpreadsheet,
      accepts: '.txt,.edi,.837,.csv',
      hint: 'EDI 837P/837I files or CSV exports',
      required: false,
      uploaded: 0,
      tier: ['small_practice', 'enterprise'],
    },
    {
      id: 'pref_cards',
      title: 'Existing Preference Cards',
      description: 'Upload equipment lists and surgical approach documents to pre-populate procedure templates.',
      icon: ClipboardList,
      accepts: '.pdf,.xlsx,.csv,.txt',
      hint: 'PDF, Excel, or CSV format',
      required: false,
      uploaded: 0,
      tier: ['single_surgeon', 'small_practice', 'enterprise'],
    },
  ]);

  const visibleZones = zones.filter((z) => z.tier.includes(tier));

  const handleSimulateUpload = (zoneId: string) => {
    setZones((prev) =>
      prev.map((z) =>
        z.id === zoneId
          ? { ...z, uploaded: z.uploaded + Math.floor(Math.random() * 20) + 5 }
          : z
      )
    );
    toast.success('Files uploaded successfully', {
      description: 'LINK will begin processing once all required uploads are complete.',
    });
  };

  const totalUploaded = visibleZones.reduce((sum, z) => sum + z.uploaded, 0);
  const requiredComplete = visibleZones
    .filter((z) => z.required)
    .every((z) => z.uploaded > 0);

  return (
    <div className="space-y-6">
      {/* HIPAA Banner */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
        <Shield className="w-5 h-5 text-primary shrink-0" />
        <p className="text-sm text-muted-foreground">
          All uploads are encrypted end-to-end and processed in a HIPAA-compliant environment. 
          PHI is never stored in plain text.
        </p>
      </div>

      {/* Upload Zones */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {visibleZones.map((zone) => {
          const Icon = zone.icon;
          const hasFiles = zone.uploaded > 0;
          return (
            <Card key={zone.id} className={`border transition-all ${hasFiles ? 'border-primary/30 bg-primary/5' : 'border-border'}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex items-center gap-2">
                    {zone.required && (
                      <Badge variant="outline" className="text-[10px] border-destructive/40 text-destructive">Required</Badge>
                    )}
                    {hasFiles && <CheckCircle2 className="w-5 h-5 text-primary" />}
                  </div>
                </div>
                <CardTitle className="text-sm mt-2">{zone.title}</CardTitle>
                <CardDescription className="text-xs">{zone.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Drop zone simulation */}
                <button
                  onClick={() => handleSimulateUpload(zone.id)}
                  className="w-full border-2 border-dashed border-border hover:border-primary/40 rounded-lg p-4 flex flex-col items-center gap-2 transition-colors group"
                >
                  <Upload className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                    Click to upload or drag files here
                  </span>
                  <span className="text-[10px] text-muted-foreground">{zone.hint}</span>
                </button>

                {hasFiles && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{zone.uploaded} files uploaded</span>
                    <Badge variant="secondary" className="text-[10px]">Processing queued</Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary & Continue */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Upload Progress</span>
                <span className="font-medium text-foreground">{totalUploaded} files total</span>
              </div>
              <Progress value={requiredComplete ? 100 : totalUploaded > 0 ? 50 : 0} className="h-2" />
              {!requiredComplete && (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <AlertCircle className="w-3 h-3" />
                  Upload at least one set of operative notes to continue
                </p>
              )}
            </div>
            <Button
              onClick={onComplete}
              disabled={!requiredComplete}
              className="shrink-0"
            >
              Begin AI Processing <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
